// Portfolio Administration v1.0 PR 7 — Row 1 (Composition).

import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Card } from "@/design-system/components/ui";
import type { PortfolioCompositionMetrics } from "@/api/portfolioAdmin/pr7Types";
import { CATEGORICAL, chartTheme } from "@/pages/dealFlow/metrics/chartTheme";
import {
  ConcentrationTable,
  MetricCard,
  MetricCardWithChart,
  MetricsCardRow,
  formatMoney,
  formatNumber,
  formatPercent,
} from "./MetricsCards";

/**
 * 🔴 Every colour on this page resolves through `chartTheme`, which reads
 * `tokens.json`. These three maps used to be hex literals — a SECOND palette,
 * unreachable from the design system, that drifted the moment a token moved.
 * `#D4A017` in particular read as brand gold and is not: brand gold is
 * `gold.DEFAULT` = `#C9A961`, which is what `chartTheme.primary` returns.
 *
 * Credit rating A→E is the one series here with no single `chartTheme` member
 * to name, because it is an ORDERED QUALITY SCALE rather than a set of states.
 * It uses `CATEGORICAL` — the sanctioned ordered palette, pinned at five
 * entries for exactly this kind of 1:1 mapping and already carrying the DQR-bin
 * and lane/tier ramps in Deal Flow and Reunderwriting. Its endpoints are
 * semantically right (A is `success`, E is `error`); its middle is a hue
 * sequence, NOT a perceptual gradient, and nothing here claims otherwise. If a
 * true ramp is ever wanted it belongs in `chartTheme` beside `CATEGORICAL`, so
 * that every consumer gets the same one — never re-derived in a row file.
 *
 * `Unrated` is the ABSENCE of a rating rather than a step on the scale, so it
 * sits off the ramp on the neutral axis grey.
 */
const RATING_COLORS: Record<string, string> = {
  A: CATEGORICAL[0],
  B: CATEGORICAL[1],
  C: CATEGORICAL[2],
  D: CATEGORICAL[3],
  E: CATEGORICAL[4],
  Unrated: chartTheme.axis,
};

/** An unrecognised rating is also "no rating we know", so it lands on the same
 *  neutral as `Unrated` rather than inventing a sixth step. */
const UNKNOWN_RATING_COLOR = chartTheme.axis;

/**
 * Segment pool composition is a LIFECYCLE, not a verdict — an unassigned
 * segment is not "bad" and a pool-locked one is not "good" — so none of the
 * status tones (`success` / `warning` / `error`) may be used here without
 * asserting a judgement the data does not carry. Brand gold for the starting
 * state, `info` for the in-flight middle, `onyx` for the terminal one: three
 * values a reader can still tell apart in a 25px-thick donut.
 */
const POOL_COLORS = [chartTheme.primary, chartTheme.info, chartTheme.onyx];

export function PortfolioCompositionRow({
  data,
}: {
  data: PortfolioCompositionMetrics;
}) {
  const ratingBars = Object.entries(data.loanCountByCreditRating).map(
    ([rating, count]) => ({ rating, count }),
  );
  const pool = data.segmentPoolComposition;
  const poolData = [
    { name: "Unassigned", value: pool.unassigned },
    { name: "Committed", value: pool.committed },
    { name: "Pool-locked", value: pool.poolLocked },
  ];

  return (
    <MetricsCardRow title="Portfolio Composition" testId="metrics-row-composition">
      <MetricCardWithChart
        label="Active loan count"
        value={formatNumber(data.activeLoanCount)}
        caption="By credit rating"
        testId="card-active-loan-count"
      >
        {ratingBars.length === 0 ? (
          <p className="text-small text-gray-mid">No loans on file.</p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={ratingBars}>
              <Bar dataKey="count">
                {ratingBars.map((entry) => (
                  <Cell
                    key={entry.rating}
                    fill={RATING_COLORS[entry.rating] ?? UNKNOWN_RATING_COLOR}
                  />
                ))}
              </Bar>
              <Tooltip />
            </BarChart>
          </ResponsiveContainer>
        )}
      </MetricCardWithChart>

      <MetricCard
        label="Aggregate outstanding principal"
        value={formatMoney(data.aggregateOutstandingPrincipal)}
        caption={`${data.activeLoanCount} active loans`}
        testId="card-aggregate-principal"
      />

      <MetricCard
        label="Average loan size"
        value={formatMoney(data.averageLoanSize)}
        caption="Mean across active portfolio"
        testId="card-avg-loan-size"
      />

      <MetricCard
        label="Weighted avg interest rate"
        value={formatPercent(data.weightedAverageInterestRate, 2)}
        caption={`Weighted by outstanding balance`}
        testId="card-wavg-rate"
      />

      <MetricCard
        label="Weighted avg remaining term"
        value={`${data.weightedAverageRemainingTermMonths.toFixed(0)} mo`}
        caption="Months to maturity, balance-weighted"
        testId="card-wavg-term"
      />

      <Card className="flex flex-col gap-space-2 p-space-3" data-testid="card-top-borrowers">
        <span className="text-caption text-gray-mid uppercase tracking-wide">
          Top 5 borrowers
        </span>
        <ConcentrationTable rows={data.topBorrowers} />
      </Card>

      <Card className="flex flex-col gap-space-2 p-space-3" data-testid="card-top-industries">
        <span className="text-caption text-gray-mid uppercase tracking-wide">
          Top 5 industries
        </span>
        <ConcentrationTable rows={data.topIndustries} />
      </Card>

      <MetricCardWithChart
        label="Segment pool composition"
        value={formatNumber(pool.unassigned + pool.committed + pool.poolLocked)}
        caption="Across all contribution segments"
        testId="card-segment-pool"
      >
        {pool.unassigned + pool.committed + pool.poolLocked === 0 ? (
          <p className="text-small text-gray-mid">No segments yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={poolData}
                dataKey="value"
                nameKey="name"
                innerRadius={30}
                outerRadius={55}
              >
                {poolData.map((_, i) => (
                  <Cell key={i} fill={POOL_COLORS[i]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        )}
      </MetricCardWithChart>
    </MetricsCardRow>
  );
}
